"use client";

import { c as _c } from "react/compiler-runtime";
import { useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { AudioLines, MessageSquare, Edit2, Trash2, MoreVerticalIcon, ImagePlus, Film, Pin, PinOff, Users, ChevronDown, ChevronUp, LogOut, ExternalLink, PenTool } from "lucide-react";
// import dynamic from "next/dynamic";
import Link from "next/link";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useTheme } from "next-themes";
import { usePostHog } from "posthog-js/react";
import { useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { List, type RowComponentProps } from "react-window";
import { toast } from "sonner";
import { CreditsDisplay } from "@/components/credits/credits-display";
import { ThemeToggle } from "@/components/landing/theme-toggle";
import { LanguageSwitcher } from "@/components/language-switcher";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Logo } from "@/components/ui/logo";
import { Sidebar, SidebarContent, SidebarFooter, SidebarHeader, SidebarMenu, SidebarMenuAction, SidebarMenuButton, SidebarMenuItem, useSidebar } from "@/components/ui/sidebar";
import { useChats, useDeleteChat, useUpdateChat, type Chat } from "@/hooks/useChats";
import { useOrganization } from "@/hooks/useOrganization";
import { useUser } from "@/hooks/useUser";
import { clearLastUsedProjectCookiesAction } from "@/lib/actions/project";
import { useAuth } from "@/lib/auth-client";
import { useI18n } from "@llmgateway/i18n/react";
import { getChatUrl, getMarketingUrl } from "@llmgateway/shared/branding";
import { ChatSidebarSkeleton } from "./chat-sidebar-skeleton";
import { OrganizationSwitcher } from "./organization-switcher";
import { SidebarChatSearch, SidebarNewAction } from "./sidebar-actions";
// import { ProjectSwitcher } from "./project-switcher";

import type { Organization, Project } from "@/lib/types";
type Translate = ReturnType<typeof useI18n>["t"];

// const OrganizationSwitcher = dynamic(
// 	() => import("./organization-switcher").then((m) => m.OrganizationSwitcher),
// 	{ ssr: false },
// );

export interface ChatSidebarHandle {
  scrollToTop: () => void;
}
interface ChatSidebarProps {
  currentChatId?: string;
  onChatSelect?: (chatId: string) => void;
  onNewChat?: () => void;
  className?: string;
  isLoading?: boolean;
  organizations: Organization[];
  selectedOrganization: Organization | null;
  onSelectOrganization: (organization: Organization | null) => void;
  onOrganizationCreated: (organization: Organization) => void;
  projects: Project[];
  selectedProject: Project | null;
  onSelectProject: (project: Project | null) => void;
  onProjectCreated: (project: Project) => void;
}
type ChatHistoryRow = {
  type: "header";
  key: string;
  title: string;
} | {
  type: "chat";
  key: string;
  chat: Chat;
} | {
  type: "spacer";
  key: string;
};
const ROW_HEIGHT_HEADER = 28;
const ROW_HEIGHT_SPACER = 6;
const ROW_HEIGHT_CHAT = 52;
const HISTORY_COLLAPSED_STORAGE_KEY = "playground-chat-history-collapsed";
interface ChatHistoryRowProps {
  rows: ChatHistoryRow[];
  currentChatId?: string;
  editingId: string | null;
  editTitle: string;
  pendingFocusChatId: string | null;
  isPageLoading: boolean;
  isMobile: boolean;
  onChatSelect?: (chatId: string) => void;
  onEditTitleChange: (value: string) => void;
  onSaveTitle: (chatId: string) => void;
  onCancelEdit: () => void;
  onDeleteChat: (chatId: string) => void;
  onTogglePin: (chat: Chat) => void;
  onStartEdit: (chat: Chat) => void;
  onEditFocused: () => void;
  t: Translate;
}
function getChatHistoryRowHeight(index: number, {
  rows
}: ChatHistoryRowProps): number {
  const row = rows[index];
  if (row?.type === "header") {
    return ROW_HEIGHT_HEADER;
  }
  if (row?.type === "spacer") {
    return ROW_HEIGHT_SPACER;
  }
  return ROW_HEIGHT_CHAT;
}
function EditChatTitleInput(t0) {
  const $ = _c(16);
  const {
    chatId,
    value,
    shouldFocus,
    onChange,
    onSave,
    onCancel,
    onFocused
  } = t0;
  const inputRef = useRef(null);
  let t1;
  let t2;
  if ($[0] !== onFocused || $[1] !== shouldFocus) {
    t1 = () => {
      if (!shouldFocus || !inputRef.current || document.activeElement === inputRef.current) {
        return;
      }
      const el = inputRef.current;
      el.focus();
      const len = el.value.length;
      el.setSelectionRange(len, len);
      onFocused();
    };
    t2 = [shouldFocus, onFocused];
    $[0] = onFocused;
    $[1] = shouldFocus;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  useEffect(t1, t2);
  let t3;
  if ($[4] !== onChange) {
    t3 = e => onChange(e.target.value);
    $[4] = onChange;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== chatId || $[7] !== onSave) {
    t4 = () => onSave(chatId);
    $[6] = chatId;
    $[7] = onSave;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== onCancel) {
    t5 = e_0 => {
      if (e_0.key === "Enter") {
        e_0.preventDefault();
        e_0.currentTarget.blur();
      }
      if (e_0.key === "Escape") {
        e_0.preventDefault();
        onCancel();
      }
    };
    $[9] = onCancel;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== t3 || $[12] !== t4 || $[13] !== t5 || $[14] !== value) {
    t6 = <Input ref={inputRef} value={value} onChange={t3} onBlur={t4} onKeyDown={t5} className="h-7 text-sm border-none px-1 focus-visible:ring-0 bg-transparent" />;
    $[11] = t3;
    $[12] = t4;
    $[13] = t5;
    $[14] = value;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  return t6;
}
type ChatHistoryItemProps = Pick<ChatHistoryRowProps, "currentChatId" | "editingId" | "editTitle" | "pendingFocusChatId" | "isPageLoading" | "isMobile" | "onChatSelect" | "onEditTitleChange" | "onSaveTitle" | "onCancelEdit" | "onDeleteChat" | "onTogglePin" | "onStartEdit" | "onEditFocused" | "t"> & {
  chat: Chat;
};
function ChatHistoryItem(t0) {
  const $ = _c(28);
  const {
    chat,
    currentChatId,
    editingId,
    editTitle,
    pendingFocusChatId,
    isPageLoading,
    isMobile,
    onChatSelect,
    onEditTitleChange,
    onSaveTitle,
    onCancelEdit,
    onDeleteChat,
    onTogglePin,
    onStartEdit,
    onEditFocused,
    t
  } = t0;
  const isEditing = editingId === chat.id;
  const isActive = currentChatId === chat.id;
  const isActionFocusable = isActive && isMobile ? undefined : -1;
  let t1;
  if ($[0] !== chat.id || $[1] !== chat.messageCount || $[2] !== chat.title || $[3] !== chat.updatedAt || $[4] !== editTitle || $[5] !== isActive || $[6] !== isEditing || $[7] !== isPageLoading || $[8] !== onCancelEdit || $[9] !== onChatSelect || $[10] !== onEditFocused || $[11] !== onEditTitleChange || $[12] !== onSaveTitle || $[13] !== pendingFocusChatId || $[14] !== t) {
    t1 = isEditing ? <div className="flex h-full w-full items-center rounded-md px-2 pr-8 text-left text-sm ring-sidebar-ring bg-sidebar-accent text-sidebar-accent-foreground"><EditChatTitleInput chatId={chat.id} value={editTitle} shouldFocus={pendingFocusChatId === chat.id} onChange={onEditTitleChange} onSave={onSaveTitle} onCancel={onCancelEdit} onFocused={onEditFocused} /></div> : <SidebarMenuButton isActive={isActive} onClick={() => onChatSelect?.(chat.id)} className={["h-full! w-full justify-start group relative pr-2 !transition-none group-hover/chat-row:pr-[4.5rem]", isActive ? "max-md:pr-[4.5rem]" : ""].join(" ")} type="button" disabled={isPageLoading}><div className="flex-1 min-w-0"><div className="truncate text-sm font-medium mb-0.5">{chat.title}</div><div className="text-xs text-muted-foreground">{t("playground.messagesCount", {
            count: chat.messageCount
          })}{" "}• {formatDate(chat.updatedAt, t)}</div></div></SidebarMenuButton>;
    $[0] = chat.id;
    $[1] = chat.messageCount;
    $[2] = chat.title;
    $[3] = chat.updatedAt;
    $[4] = editTitle;
    $[5] = isActive;
    $[6] = isEditing;
    $[7] = isPageLoading;
    $[8] = onCancelEdit;
    $[9] = onChatSelect;
    $[10] = onEditFocused;
    $[11] = onEditTitleChange;
    $[12] = onSaveTitle;
    $[13] = pendingFocusChatId;
    $[14] = t;
    $[15] = t1;
  } else {
    t1 = $[15];
  }
  let t2;
  if ($[16] !== chat || $[17] !== isActionFocusable || $[18] !== isActive || $[19] !== isEditing || $[20] !== onDeleteChat || $[21] !== onStartEdit || $[22] !== onTogglePin || $[23] !== t) {
    t2 = !isEditing && <div className="absolute right-1 top-1/2 flex -translate-y-1/2 items-center gap-0.5"><button type="button" aria-label={chat.pinned ? t("playground.unpin") : t("playground.pin")} title={chat.pinned ? t("playground.unpin") : t("playground.pin")} tabIndex={isActionFocusable} onClick={e => {
        e.stopPropagation();
        onTogglePin(chat);
      }} className={["flex h-7 w-7 items-center justify-center rounded-md text-sidebar-foreground outline-hidden transition-opacity hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:ring-2 focus-visible:ring-sidebar-ring", "pointer-events-none opacity-0 group-hover/chat-row:pointer-events-auto group-hover/chat-row:opacity-100", isActive ? "max-md:pointer-events-auto max-md:opacity-100" : ""].join(" ")}>{chat.pinned ? <PinOff className="h-3.5 w-3.5" /> : <Pin className="h-3.5 w-3.5" />}</button><DropdownMenu><DropdownMenuTrigger asChild={true}><SidebarMenuAction type="button" tabIndex={isActionFocusable} onClick={_temp} className={["pointer-events-none static hidden h-7 w-7 cursor-pointer opacity-0 group-hover/chat-row:flex group-hover/chat-row:pointer-events-auto group-hover/chat-row:opacity-100 data-[state=open]:flex data-[state=open]:pointer-events-auto data-[state=open]:opacity-100", isActive ? "max-md:flex max-md:pointer-events-auto max-md:opacity-100" : ""].join(" ")}><MoreVerticalIcon className="h-3.5 w-3.5" /></SidebarMenuAction></DropdownMenuTrigger><DropdownMenuContent align="end" className="w-48"><DropdownMenuItem onClick={e_1 => {
            e_1.stopPropagation();
            onTogglePin(chat);
          }} className="flex items-center gap-2">{chat.pinned ? <PinOff className="h-4 w-4" /> : <Pin className="h-4 w-4" />}{chat.pinned ? t("playground.unpin") : t("playground.pin")}</DropdownMenuItem><DropdownMenuItem onClick={e_2 => {
            e_2.stopPropagation();
            onStartEdit(chat);
          }} className="flex items-center gap-2"><Edit2 className="h-4 w-4" />{t("playground.rename")}</DropdownMenuItem><DropdownMenuItem onClick={e_3 => {
            e_3.stopPropagation();
            onDeleteChat(chat.id);
          }} className="flex items-center gap-2 text-destructive focus:text-destructive"><Trash2 className="h-4 w-4" />{t("common.delete")}</DropdownMenuItem></DropdownMenuContent></DropdownMenu></div>;
    $[16] = chat;
    $[17] = isActionFocusable;
    $[18] = isActive;
    $[19] = isEditing;
    $[20] = onDeleteChat;
    $[21] = onStartEdit;
    $[22] = onTogglePin;
    $[23] = t;
    $[24] = t2;
  } else {
    t2 = $[24];
  }
  let t3;
  if ($[25] !== t1 || $[26] !== t2) {
    t3 = <div className="relative h-full px-2 pb-1"><div className="group/chat-row relative h-full">{t1}{t2}</div></div>;
    $[25] = t1;
    $[26] = t2;
    $[27] = t3;
  } else {
    t3 = $[27];
  }
  return t3;
}
function _temp(e_0) {
  e_0.stopPropagation();
}
function ChatHistoryRowComponent(t0) {
  const $ = _c(29);
  const {
    ariaAttributes,
    index,
    style,
    rows,
    currentChatId,
    editingId,
    editTitle,
    pendingFocusChatId,
    isPageLoading,
    isMobile,
    onChatSelect,
    onEditTitleChange,
    onSaveTitle,
    onCancelEdit,
    onDeleteChat,
    onTogglePin,
    onStartEdit,
    onEditFocused,
    t
  } = t0;
  const row = rows[index];
  if (!row) {
    return null;
  }
  if (row.type === "spacer") {
    let t1;
    if ($[0] !== style) {
      t1 = <div style={style} aria-hidden="true" />;
      $[0] = style;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    return t1;
  }
  if (row.type === "header") {
    let t1;
    if ($[2] !== row.title) {
      t1 = <div className="px-5 py-2 text-xs font-medium text-muted-foreground uppercase tracking-wider group-data-[collapsible=icon]:hidden">{row.title}</div>;
      $[2] = row.title;
      $[3] = t1;
    } else {
      t1 = $[3];
    }
    let t2;
    if ($[4] !== ariaAttributes || $[5] !== style || $[6] !== t1) {
      t2 = <div {...ariaAttributes} style={style}>{t1}</div>;
      $[4] = ariaAttributes;
      $[5] = style;
      $[6] = t1;
      $[7] = t2;
    } else {
      t2 = $[7];
    }
    return t2;
  }
  let t1;
  if ($[8] !== currentChatId || $[9] !== editTitle || $[10] !== editingId || $[11] !== isMobile || $[12] !== isPageLoading || $[13] !== onCancelEdit || $[14] !== onChatSelect || $[15] !== onDeleteChat || $[16] !== onEditFocused || $[17] !== onEditTitleChange || $[18] !== onSaveTitle || $[19] !== onStartEdit || $[20] !== onTogglePin || $[21] !== pendingFocusChatId || $[22] !== row.chat || $[23] !== t) {
    t1 = <ChatHistoryItem chat={row.chat} currentChatId={currentChatId} editingId={editingId} editTitle={editTitle} pendingFocusChatId={pendingFocusChatId} isPageLoading={isPageLoading} isMobile={isMobile} onChatSelect={onChatSelect} onEditTitleChange={onEditTitleChange} onSaveTitle={onSaveTitle} onCancelEdit={onCancelEdit} onDeleteChat={onDeleteChat} onTogglePin={onTogglePin} onStartEdit={onStartEdit} onEditFocused={onEditFocused} t={t} />;
    $[8] = currentChatId;
    $[9] = editTitle;
    $[10] = editingId;
    $[11] = isMobile;
    $[12] = isPageLoading;
    $[13] = onCancelEdit;
    $[14] = onChatSelect;
    $[15] = onDeleteChat;
    $[16] = onEditFocused;
    $[17] = onEditTitleChange;
    $[18] = onSaveTitle;
    $[19] = onStartEdit;
    $[20] = onTogglePin;
    $[21] = pendingFocusChatId;
    $[22] = row.chat;
    $[23] = t;
    $[24] = t1;
  } else {
    t1 = $[24];
  }
  let t2;
  if ($[25] !== ariaAttributes || $[26] !== style || $[27] !== t1) {
    t2 = <div {...ariaAttributes} style={style}>{t1}</div>;
    $[25] = ariaAttributes;
    $[26] = style;
    $[27] = t1;
    $[28] = t2;
  } else {
    t2 = $[28];
  }
  return t2;
}
function formatDate(dateString: string, t: Translate): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);
  if (diffInHours < 1) {
    return t("time.justNow");
  } else if (diffInHours < 24) {
    return t("time.hoursAgo", {
      count: Math.floor(diffInHours)
    });
  } else if (diffInHours < 48) {
    return t("time.yesterday");
  } else {
    return format(date, "MMM d");
  }
}
function groupChatsByDate(chats: Chat[]) {
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const lastWeek = new Date(today);
  lastWeek.setDate(lastWeek.getDate() - 7);
  const groups = {
    today: [] as Chat[],
    yesterday: [] as Chat[],
    lastWeek: [] as Chat[],
    older: [] as Chat[]
  };
  chats.forEach(chat => {
    const chatDate = new Date(chat.updatedAt);
    if (chatDate.toDateString() === today.toDateString()) {
      groups.today.push(chat);
    } else if (chatDate.toDateString() === yesterday.toDateString()) {
      groups.yesterday.push(chat);
    } else if (chatDate >= lastWeek) {
      groups.lastWeek.push(chat);
    } else {
      groups.older.push(chat);
    }
  });
  return groups;
}
export const ChatSidebar = function ChatSidebar(t0) {
  const $ = _c(328);
  const {
    ref,
    currentChatId,
    onChatSelect,
    onNewChat,
    className,
    isLoading: t1,
    organizations,
    selectedOrganization,
    onSelectOrganization
  } = t0;
  const isPageLoading = t1 === undefined ? false : t1;
  const {
    t
  } = useI18n();
  const listContainerRef = useRef(null);
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => ({
      scrollToTop: () => {
        const scrollEl = listContainerRef.current?.firstElementChild as HTMLElement | null;
        scrollEl?.scrollTo({
          top: 0,
          behavior: "smooth"
        });
      }
    });
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  useImperativeHandle(ref, t2);
  const queryClient = useQueryClient();
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  let t3;
  if ($[1] !== searchParams) {
    t3 = searchParams.get("orgId");
    $[1] = searchParams;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const orgIdParam = t3;
  let t4;
  if ($[3] !== orgIdParam) {
    t4 = path => orgIdParam ? `${path}?orgId=${orgIdParam}` : path;
    $[3] = orgIdParam;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const withOrg = t4;
  const posthog = usePostHog();
  const {
    state: sidebarState,
    isMobile,
    setOpenMobile
  } = useSidebar();
  const showOrganizationSwitcher = pathname === "/" || pathname === "/group";
  const {
    user,
    isLoading: isUserLoading
  } = useUser();
  const {
    signOut
  } = useAuth();
  const {
    organization,
    isLoading: isOrgLoading
  } = useOrganization();
  const {
    theme,
    setTheme,
    systemTheme
  } = useTheme();
  const resolvedOrgId = selectedOrganization?.id ?? organization?.id;
  const {
    data: chatsData,
    isLoading: isChatsLoading
  } = useChats(resolvedOrgId);
  const deleteChat = useDeleteChat();
  const updateChat = useUpdateChat();
  const [editingId, setEditingId] = useState(null);
  const [editTitle, setEditTitle] = useState("");
  const [pendingFocusChatId, setPendingFocusChatId] = useState(null);
  const [isHistoryCollapsed, setIsHistoryCollapsed] = useState(_temp2);
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => {
      setIsHistoryCollapsed(_temp3);
    };
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const toggleHistoryCollapsed = t5;
  chatsData?.chats;
  let t6;
  if ($[6] !== chatsData?.chats) {
    t6 = chatsData?.chats ?? [];
    $[6] = chatsData?.chats;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  const chats = t6;
  const currentTheme = theme === "system" ? systemTheme : theme;
  let t7;
  if ($[8] !== currentTheme || $[9] !== setTheme) {
    t7 = () => {
      setTheme(currentTheme === "dark" ? "light" : "dark");
    };
    $[8] = currentTheme;
    $[9] = setTheme;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  const toggleTheme = t7;
  let t8;
  if ($[11] !== posthog || $[12] !== queryClient || $[13] !== router || $[14] !== signOut || $[15] !== t) {
    t8 = async () => {
      posthog.reset();
      try {
        await clearLastUsedProjectCookiesAction();
      } catch {
        toast.error(t("common.unknownError"));
      }
      await signOut({
        fetchOptions: {
          onSuccess: () => {
            queryClient.clear();
            router.push(process.env.NODE_ENV === "development" ? "http://localhost:3003/login" : getChatUrl("/login"));
          }
        }
      });
    };
    $[11] = posthog;
    $[12] = queryClient;
    $[13] = router;
    $[14] = signOut;
    $[15] = t;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  const logout = t8;
  let t9;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = chat => {
      setEditingId(chat.id);
      setEditTitle(chat.title);
      setPendingFocusChatId(chat.id);
    };
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  const handleEditTitle = t9;
  let t10;
  if ($[18] !== chats || $[19] !== editTitle || $[20] !== updateChat) {
    t10 = chatId => {
      const nextTitle = editTitle.trim();
      const currentTitle = chats.find(chat_0 => chat_0.id === chatId)?.title.trim();
      if (nextTitle && nextTitle !== currentTitle) {
        updateChat.mutate({
          params: {
            path: {
              id: chatId
            }
          },
          body: {
            title: nextTitle
          }
        });
      }
      setEditingId(null);
      setEditTitle("");
      setPendingFocusChatId(null);
    };
    $[18] = chats;
    $[19] = editTitle;
    $[20] = updateChat;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  const saveTitle = t10;
  let t11;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = () => {
      setEditingId(null);
      setEditTitle("");
      setPendingFocusChatId(null);
    };
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  const cancelEditTitle = t11;
  let t12;
  if ($[23] !== currentChatId || $[24] !== deleteChat || $[25] !== onNewChat) {
    t12 = chatId_0 => {
      deleteChat.mutate({
        params: {
          path: {
            id: chatId_0
          }
        }
      });
      if (currentChatId === chatId_0) {
        onNewChat?.();
      }
    };
    $[23] = currentChatId;
    $[24] = deleteChat;
    $[25] = onNewChat;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  const handleDeleteChat = t12;
  let t13;
  if ($[27] !== isMobile || $[28] !== onChatSelect || $[29] !== setOpenMobile) {
    t13 = chatId_1 => {
      onChatSelect?.(chatId_1);
      if (isMobile) {
        setOpenMobile(false);
      }
    };
    $[27] = isMobile;
    $[28] = onChatSelect;
    $[29] = setOpenMobile;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  const handleChatSelect = t13;
  let t14;
  if ($[31] !== updateChat) {
    t14 = chat_1 => {
      updateChat.mutate({
        params: {
          path: {
            id: chat_1.id
          }
        },
        body: {
          pinned: !chat_1.pinned
        }
      });
    };
    $[31] = updateChat;
    $[32] = t14;
  } else {
    t14 = $[32];
  }
  const handleTogglePin = t14;
  let t15;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = () => {
      setPendingFocusChatId(null);
    };
    $[33] = t15;
  } else {
    t15 = $[33];
  }
  const onEditFocused = t15;
  let t16;
  if ($[34] !== chats) {
    t16 = [...chats].sort(_temp4);
    $[34] = chats;
    $[35] = t16;
  } else {
    t16 = $[35];
  }
  const sortedChats = t16;
  let t17;
  if ($[36] !== sortedChats) {
    t17 = sortedChats.filter(_temp5);
    $[36] = sortedChats;
    $[37] = t17;
  } else {
    t17 = $[37];
  }
  const pinnedChats = t17;
  let t18;
  if ($[38] !== sortedChats) {
    t18 = groupChatsByDate(sortedChats.filter(_temp6));
    $[38] = sortedChats;
    $[39] = t18;
  } else {
    t18 = $[39];
  }
  const chatGroups = t18;
  let rows;
  if ($[40] !== chatGroups.lastWeek || $[41] !== chatGroups.older || $[42] !== chatGroups.today || $[43] !== chatGroups.yesterday || $[44] !== t) {
    const groups = [{
      title: t("playground.today"),
      chats: chatGroups.today
    }, {
      title: t("playground.yesterday"),
      chats: chatGroups.yesterday
    }, {
      title: t("playground.last7Days"),
      chats: chatGroups.lastWeek
    }, {
      title: t("playground.older"),
      chats: chatGroups.older
    }];
    rows = [];
    groups.forEach((t19, groupIndex) => {
      const {
        title,
        chats: groupedChats
      } = t19;
      if (groupedChats.length === 0) {
        return;
      }
      rows.push({
        type: "header",
        key: `header-${title}`,
        title
      });
      groupedChats.forEach(chat_4 => {
        rows.push({
          type: "chat",
          key: `chat-${chat_4.id}`,
          chat: chat_4
        });
      });
      const hasNextGroupWithChats = groups.slice(groupIndex + 1).some(_temp7);
      if (hasNextGroupWithChats) {
        rows.push({
          type: "spacer",
          key: `spacer-${title}`
        });
      }
    });
    $[40] = chatGroups.lastWeek;
    $[41] = chatGroups.older;
    $[42] = chatGroups.today;
    $[43] = chatGroups.yesterday;
    $[44] = t;
    $[45] = rows;
  } else {
    rows = $[45];
  }
  const historyRows = rows;
  let t19;
  if ($[46] !== currentChatId || $[47] !== editTitle || $[48] !== editingId || $[49] !== handleChatSelect || $[50] !== handleDeleteChat || $[51] !== handleTogglePin || $[52] !== historyRows || $[53] !== isMobile || $[54] !== isPageLoading || $[55] !== pendingFocusChatId || $[56] !== saveTitle || $[57] !== t) {
    t19 = {
      rows: historyRows,
      currentChatId,
      editingId,
      editTitle,
      pendingFocusChatId,
      isPageLoading,
      isMobile,
      onChatSelect: handleChatSelect,
      onEditTitleChange: setEditTitle,
      onSaveTitle: saveTitle,
      onCancelEdit: cancelEditTitle,
      onDeleteChat: handleDeleteChat,
      onTogglePin: handleTogglePin,
      onStartEdit: handleEditTitle,
      onEditFocused,
      t
    };
    $[46] = currentChatId;
    $[47] = editTitle;
    $[48] = editingId;
    $[49] = handleChatSelect;
    $[50] = handleDeleteChat;
    $[51] = handleTogglePin;
    $[52] = historyRows;
    $[53] = isMobile;
    $[54] = isPageLoading;
    $[55] = pendingFocusChatId;
    $[56] = saveTitle;
    $[57] = t;
    $[58] = t19;
  } else {
    t19 = $[58];
  }
  const rowProps = t19;
  const isAuthenticated = !!user;
  const isHistoryHidden = sidebarState === "collapsed" && !isMobile;
  if (isUserLoading) {
    let t20;
    if ($[59] === Symbol.for("react.memo_cache_sentinel")) {
      t20 = <ChatSidebarSkeleton organization={null} isOrgLoading={true} />;
      $[59] = t20;
    } else {
      t20 = $[59];
    }
    return t20;
  }
  if (!isAuthenticated) {
    let t20;
    let t21;
    if ($[60] === Symbol.for("react.memo_cache_sentinel")) {
      t20 = <Logo className="size-6" />;
      t21 = <h1 className="text-xl font-semibold">doteb</h1>;
      $[60] = t20;
      $[61] = t21;
    } else {
      t20 = $[60];
      t21 = $[61];
    }
    let t22;
    if ($[62] !== t) {
      t22 = t("playground.chat");
      $[62] = t;
      $[63] = t22;
    } else {
      t22 = $[63];
    }
    let t23;
    if ($[64] !== t22) {
      t23 = <Link href="/" className="flex self-start items-center gap-2 my-2" prefetch={true}>{t20}{t21}<Badge>{t22}</Badge></Link>;
      $[64] = t22;
      $[65] = t23;
    } else {
      t23 = $[65];
    }
    let t24;
    if ($[66] === Symbol.for("react.memo_cache_sentinel")) {
      t24 = <LanguageSwitcher className="self-end" />;
      $[66] = t24;
    } else {
      t24 = $[66];
    }
    let t25;
    if ($[67] !== t) {
      t25 = t("playground.signInRequired");
      $[67] = t;
      $[68] = t25;
    } else {
      t25 = $[68];
    }
    let t26;
    if ($[69] !== t25) {
      t26 = <div className="font-medium mb-2">{t25}</div>;
      $[69] = t25;
      $[70] = t26;
    } else {
      t26 = $[70];
    }
    let t27;
    if ($[71] !== t) {
      t27 = t("playground.signInRequiredDescription");
      $[71] = t;
      $[72] = t27;
    } else {
      t27 = $[72];
    }
    let t28;
    if ($[73] !== t27) {
      t28 = <p className="text-muted-foreground mb-3">{t27}</p>;
      $[73] = t27;
      $[74] = t28;
    } else {
      t28 = $[74];
    }
    let t29;
    if ($[75] !== t) {
      t29 = t("auth.signIn");
      $[75] = t;
      $[76] = t29;
    } else {
      t29 = $[76];
    }
    let t30;
    if ($[77] !== t29) {
      t30 = <Button size="sm" asChild={true}><Link href="/login">{t29}</Link></Button>;
      $[77] = t29;
      $[78] = t30;
    } else {
      t30 = $[78];
    }
    let t31;
    if ($[79] !== t) {
      t31 = t("auth.createAccount");
      $[79] = t;
      $[80] = t31;
    } else {
      t31 = $[80];
    }
    let t32;
    if ($[81] !== t31) {
      t32 = <Button size="sm" variant="outline" asChild={true}><Link href="/signup">{t31}</Link></Button>;
      $[81] = t31;
      $[82] = t32;
    } else {
      t32 = $[82];
    }
    let t33;
    if ($[83] !== t30 || $[84] !== t32) {
      t33 = <div className="flex items-center justify-end gap-2">{t30}{t32}</div>;
      $[83] = t30;
      $[84] = t32;
      $[85] = t33;
    } else {
      t33 = $[85];
    }
    let t34;
    if ($[86] !== t26 || $[87] !== t28 || $[88] !== t33) {
      t34 = <div className="w-full rounded-md border p-4 text-sm">{t26}{t28}{t33}</div>;
      $[86] = t26;
      $[87] = t28;
      $[88] = t33;
      $[89] = t34;
    } else {
      t34 = $[89];
    }
    let t35;
    if ($[90] !== t23 || $[91] !== t34) {
      t35 = <SidebarHeader><div className="flex flex-col items-center gap-4 mb-4">{t23}{t24}{t34}</div></SidebarHeader>;
      $[90] = t23;
      $[91] = t34;
      $[92] = t35;
    } else {
      t35 = $[92];
    }
    let t36;
    if ($[93] !== className || $[94] !== t35) {
      t36 = <Sidebar className={className}>{t35}</Sidebar>;
      $[93] = className;
      $[94] = t35;
      $[95] = t36;
    } else {
      t36 = $[95];
    }
    return t36;
  }
  if (isChatsLoading || isOrgLoading) {
    let t20;
    if ($[96] !== isOrgLoading || $[97] !== selectedOrganization) {
      t20 = <ChatSidebarSkeleton organization={selectedOrganization} isOrgLoading={isOrgLoading} />;
      $[96] = isOrgLoading;
      $[97] = selectedOrganization;
      $[98] = t20;
    } else {
      t20 = $[98];
    }
    return t20;
  }
  const t20 = className + " max-md:hidden";
  let t21;
  if ($[99] !== withOrg) {
    t21 = withOrg("/");
    $[99] = withOrg;
    $[100] = t21;
  } else {
    t21 = $[100];
  }
  let t22;
  let t23;
  if ($[101] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <div className="flex aspect-square size-8 items-center justify-center"><Logo className="size-6" /></div>;
    t23 = <span className="text-lg font-bold tracking-tight">doteb</span>;
    $[101] = t22;
    $[102] = t23;
  } else {
    t22 = $[101];
    t23 = $[102];
  }
  let t24;
  if ($[103] !== t) {
    t24 = t("playground.chat");
    $[103] = t;
    $[104] = t24;
  } else {
    t24 = $[104];
  }
  let t25;
  if ($[105] !== t24) {
    t25 = <Badge variant="secondary" className="group-data-[collapsible=icon]:hidden">{t24}</Badge>;
    $[105] = t24;
    $[106] = t25;
  } else {
    t25 = $[106];
  }
  let t26;
  if ($[107] !== t21 || $[108] !== t25) {
    t26 = <SidebarMenuItem><SidebarMenuButton size="lg" asChild={true} tooltip="doteb"><Link href={t21} prefetch={true}>{t22}{t23}{t25}</Link></SidebarMenuButton></SidebarMenuItem>;
    $[107] = t21;
    $[108] = t25;
    $[109] = t26;
  } else {
    t26 = $[109];
  }
  const t27 = pathname !== "/";
  let t28;
  if ($[110] !== handleChatSelect || $[111] !== onNewChat || $[112] !== t27) {
    t28 = <SidebarChatSearch disabled={t27} onChatSelect={handleChatSelect} onNewChat={onNewChat} />;
    $[110] = handleChatSelect;
    $[111] = onNewChat;
    $[112] = t27;
    $[113] = t28;
  } else {
    t28 = $[113];
  }
  let t29;
  if ($[114] !== t) {
    t29 = t("playground.newChat");
    $[114] = t;
    $[115] = t29;
  } else {
    t29 = $[115];
  }
  let t30;
  if ($[116] !== isPageLoading || $[117] !== onNewChat || $[118] !== t29) {
    t30 = <SidebarNewAction label={t29} onAction={onNewChat} isLoading={isPageLoading} />;
    $[116] = isPageLoading;
    $[117] = onNewChat;
    $[118] = t29;
    $[119] = t30;
  } else {
    t30 = $[119];
  }
  let t31;
  if ($[120] !== t) {
    t31 = t("playground.chat");
    $[120] = t;
    $[121] = t31;
  } else {
    t31 = $[121];
  }
  const t32 = pathname === "/";
  let t33;
  if ($[122] !== withOrg) {
    t33 = withOrg("/");
    $[122] = withOrg;
    $[123] = t33;
  } else {
    t33 = $[123];
  }
  let t34;
  if ($[124] === Symbol.for("react.memo_cache_sentinel")) {
    t34 = <MessageSquare className="h-4 w-4" />;
    $[124] = t34;
  } else {
    t34 = $[124];
  }
  let t35;
  if ($[125] !== t) {
    t35 = t("playground.chat");
    $[125] = t;
    $[126] = t35;
  } else {
    t35 = $[126];
  }
  let t36;
  if ($[127] !== t35) {
    t36 = <span>{t35}</span>;
    $[127] = t35;
    $[128] = t36;
  } else {
    t36 = $[128];
  }
  let t37;
  if ($[129] !== t33 || $[130] !== t36) {
    t37 = <Link href={t33} prefetch={true}>{t34}{t36}</Link>;
    $[129] = t33;
    $[130] = t36;
    $[131] = t37;
  } else {
    t37 = $[131];
  }
  let t38;
  if ($[132] !== t31 || $[133] !== t32 || $[134] !== t37) {
    t38 = <SidebarMenuItem><SidebarMenuButton asChild={true} tooltip={t31} isActive={t32}>{t37}</SidebarMenuButton></SidebarMenuItem>;
    $[132] = t31;
    $[133] = t32;
    $[134] = t37;
    $[135] = t38;
  } else {
    t38 = $[135];
  }
  let t39;
  if ($[136] !== t) {
    t39 = t("playground.groupChat");
    $[136] = t;
    $[137] = t39;
  } else {
    t39 = $[137];
  }
  const t40 = pathname === "/group";
  let t41;
  if ($[138] !== withOrg) {
    t41 = withOrg("/group");
    $[138] = withOrg;
    $[139] = t41;
  } else {
    t41 = $[139];
  }
  let t42;
  if ($[140] === Symbol.for("react.memo_cache_sentinel")) {
    t42 = <Users className="h-4 w-4" />;
    $[140] = t42;
  } else {
    t42 = $[140];
  }
  let t43;
  if ($[141] !== t) {
    t43 = t("playground.groupChat");
    $[141] = t;
    $[142] = t43;
  } else {
    t43 = $[142];
  }
  let t44;
  if ($[143] !== t43) {
    t44 = <span>{t43}</span>;
    $[143] = t43;
    $[144] = t44;
  } else {
    t44 = $[144];
  }
  let t45;
  if ($[145] !== t41 || $[146] !== t44) {
    t45 = <Link href={t41} prefetch={true}>{t42}{t44}</Link>;
    $[145] = t41;
    $[146] = t44;
    $[147] = t45;
  } else {
    t45 = $[147];
  }
  let t46;
  if ($[148] !== t39 || $[149] !== t40 || $[150] !== t45) {
    t46 = <SidebarMenuItem><SidebarMenuButton asChild={true} tooltip={t39} isActive={t40}>{t45}</SidebarMenuButton></SidebarMenuItem>;
    $[148] = t39;
    $[149] = t40;
    $[150] = t45;
    $[151] = t46;
  } else {
    t46 = $[151];
  }
  let t47;
  if ($[152] !== t) {
    t47 = t("playground.imageStudio");
    $[152] = t;
    $[153] = t47;
  } else {
    t47 = $[153];
  }
  const t48 = pathname === "/image";
  let t49;
  if ($[154] !== withOrg) {
    t49 = withOrg("/image");
    $[154] = withOrg;
    $[155] = t49;
  } else {
    t49 = $[155];
  }
  let t50;
  if ($[156] === Symbol.for("react.memo_cache_sentinel")) {
    t50 = <ImagePlus className="h-4 w-4" />;
    $[156] = t50;
  } else {
    t50 = $[156];
  }
  let t51;
  if ($[157] !== t) {
    t51 = t("playground.imageStudio");
    $[157] = t;
    $[158] = t51;
  } else {
    t51 = $[158];
  }
  let t52;
  if ($[159] !== t51) {
    t52 = <span>{t51}</span>;
    $[159] = t51;
    $[160] = t52;
  } else {
    t52 = $[160];
  }
  let t53;
  if ($[161] !== t49 || $[162] !== t52) {
    t53 = <Link href={t49} prefetch={true}>{t50}{t52}</Link>;
    $[161] = t49;
    $[162] = t52;
    $[163] = t53;
  } else {
    t53 = $[163];
  }
  let t54;
  if ($[164] !== t47 || $[165] !== t48 || $[166] !== t53) {
    t54 = <SidebarMenuItem><SidebarMenuButton asChild={true} tooltip={t47} isActive={t48}>{t53}</SidebarMenuButton></SidebarMenuItem>;
    $[164] = t47;
    $[165] = t48;
    $[166] = t53;
    $[167] = t54;
  } else {
    t54 = $[167];
  }
  let t55;
  if ($[168] !== t) {
    t55 = t("playground.videoStudio");
    $[168] = t;
    $[169] = t55;
  } else {
    t55 = $[169];
  }
  const t56 = pathname === "/video";
  let t57;
  if ($[170] !== withOrg) {
    t57 = withOrg("/video");
    $[170] = withOrg;
    $[171] = t57;
  } else {
    t57 = $[171];
  }
  let t58;
  if ($[172] === Symbol.for("react.memo_cache_sentinel")) {
    t58 = <Film className="h-4 w-4" />;
    $[172] = t58;
  } else {
    t58 = $[172];
  }
  let t59;
  if ($[173] !== t) {
    t59 = t("playground.videoStudio");
    $[173] = t;
    $[174] = t59;
  } else {
    t59 = $[174];
  }
  let t60;
  if ($[175] !== t59) {
    t60 = <span>{t59}</span>;
    $[175] = t59;
    $[176] = t60;
  } else {
    t60 = $[176];
  }
  let t61;
  if ($[177] !== t57 || $[178] !== t60) {
    t61 = <Link href={t57} prefetch={true}>{t58}{t60}</Link>;
    $[177] = t57;
    $[178] = t60;
    $[179] = t61;
  } else {
    t61 = $[179];
  }
  let t62;
  if ($[180] !== t55 || $[181] !== t56 || $[182] !== t61) {
    t62 = <SidebarMenuItem><SidebarMenuButton asChild={true} tooltip={t55} isActive={t56}>{t61}</SidebarMenuButton></SidebarMenuItem>;
    $[180] = t55;
    $[181] = t56;
    $[182] = t61;
    $[183] = t62;
  } else {
    t62 = $[183];
  }
  let t63;
  if ($[184] !== t) {
    t63 = t("playground.audioStudio");
    $[184] = t;
    $[185] = t63;
  } else {
    t63 = $[185];
  }
  const t64 = pathname === "/audio";
  let t65;
  if ($[186] !== withOrg) {
    t65 = withOrg("/audio");
    $[186] = withOrg;
    $[187] = t65;
  } else {
    t65 = $[187];
  }
  let t66;
  if ($[188] === Symbol.for("react.memo_cache_sentinel")) {
    t66 = <AudioLines className="h-4 w-4" />;
    $[188] = t66;
  } else {
    t66 = $[188];
  }
  let t67;
  if ($[189] !== t) {
    t67 = t("playground.audioStudio");
    $[189] = t;
    $[190] = t67;
  } else {
    t67 = $[190];
  }
  let t68;
  if ($[191] !== t67) {
    t68 = <span>{t67}</span>;
    $[191] = t67;
    $[192] = t68;
  } else {
    t68 = $[192];
  }
  let t69;
  if ($[193] !== t65 || $[194] !== t68) {
    t69 = <Link href={t65} prefetch={true}>{t66}{t68}</Link>;
    $[193] = t65;
    $[194] = t68;
    $[195] = t69;
  } else {
    t69 = $[195];
  }
  let t70;
  if ($[196] !== t63 || $[197] !== t64 || $[198] !== t69) {
    t70 = <SidebarMenuItem><SidebarMenuButton asChild={true} tooltip={t63} isActive={t64}>{t69}</SidebarMenuButton></SidebarMenuItem>;
    $[196] = t63;
    $[197] = t64;
    $[198] = t69;
    $[199] = t70;
  } else {
    t70 = $[199];
  }
  let t71;
  if ($[200] !== t) {
    t71 = t("playground.canvas");
    $[200] = t;
    $[201] = t71;
  } else {
    t71 = $[201];
  }
  const t72 = pathname === "/canvas";
  let t73;
  if ($[202] !== withOrg) {
    t73 = withOrg("/canvas");
    $[202] = withOrg;
    $[203] = t73;
  } else {
    t73 = $[203];
  }
  let t74;
  if ($[204] === Symbol.for("react.memo_cache_sentinel")) {
    t74 = <PenTool className="h-4 w-4" />;
    $[204] = t74;
  } else {
    t74 = $[204];
  }
  let t75;
  if ($[205] !== t) {
    t75 = t("playground.canvas");
    $[205] = t;
    $[206] = t75;
  } else {
    t75 = $[206];
  }
  let t76;
  if ($[207] !== t75) {
    t76 = <span>{t75}</span>;
    $[207] = t75;
    $[208] = t76;
  } else {
    t76 = $[208];
  }
  let t77;
  if ($[209] !== t73 || $[210] !== t76) {
    t77 = <Link href={t73} prefetch={true}>{t74}{t76}</Link>;
    $[209] = t73;
    $[210] = t76;
    $[211] = t77;
  } else {
    t77 = $[211];
  }
  let t78;
  if ($[212] !== t71 || $[213] !== t72 || $[214] !== t77) {
    t78 = <SidebarMenuItem><SidebarMenuButton asChild={true} tooltip={t71} isActive={t72}>{t77}</SidebarMenuButton></SidebarMenuItem>;
    $[212] = t71;
    $[213] = t72;
    $[214] = t77;
    $[215] = t78;
  } else {
    t78 = $[215];
  }
  let t79;
  if ($[216] !== t26 || $[217] !== t28 || $[218] !== t30 || $[219] !== t38 || $[220] !== t46 || $[221] !== t54 || $[222] !== t62 || $[223] !== t70 || $[224] !== t78) {
    t79 = <SidebarHeader><SidebarMenu>{t26}{t28}{t30}{t38}{t46}{t54}{t62}{t70}{t78}</SidebarMenu></SidebarHeader>;
    $[216] = t26;
    $[217] = t28;
    $[218] = t30;
    $[219] = t38;
    $[220] = t46;
    $[221] = t54;
    $[222] = t62;
    $[223] = t70;
    $[224] = t78;
    $[225] = t79;
  } else {
    t79 = $[225];
  }
  let t80;
  if ($[226] === Symbol.for("react.memo_cache_sentinel")) {
    t80 = <div className="mx-2 mb-2 border-t border-sidebar-border" />;
    $[226] = t80;
  } else {
    t80 = $[226];
  }
  let t81;
  if ($[227] !== onSelectOrganization || $[228] !== organizations || $[229] !== selectedOrganization || $[230] !== showOrganizationSwitcher) {
    t81 = organizations.length > 0 && showOrganizationSwitcher ? <><SidebarMenu className="px-2 pb-2 group-data-[collapsible=icon]:hidden"><SidebarMenuItem><OrganizationSwitcher organizations={organizations} selectedOrganization={selectedOrganization} onSelectOrganization={onSelectOrganization} /></SidebarMenuItem></SidebarMenu></> : null;
    $[227] = onSelectOrganization;
    $[228] = organizations;
    $[229] = selectedOrganization;
    $[230] = showOrganizationSwitcher;
    $[231] = t81;
  } else {
    t81 = $[231];
  }
  let t82;
  if ($[232] !== t81) {
    t82 = <div>{t80}{t81}</div>;
    $[232] = t81;
    $[233] = t82;
  } else {
    t82 = $[233];
  }
  const t83 = !isHistoryCollapsed;
  let t84;
  if ($[234] !== t) {
    t84 = t("playground.chats");
    $[234] = t;
    $[235] = t84;
  } else {
    t84 = $[235];
  }
  let t85;
  if ($[236] !== t84) {
    t85 = <span>{t84}</span>;
    $[236] = t84;
    $[237] = t85;
  } else {
    t85 = $[237];
  }
  const t86 = `h-3.5 w-3.5 transition-transform duration-200 ${isHistoryCollapsed ? "-rotate-90" : ""}`;
  let t87;
  if ($[238] !== t86) {
    t87 = <ChevronDown className={t86} />;
    $[238] = t86;
    $[239] = t87;
  } else {
    t87 = $[239];
  }
  let t88;
  if ($[240] !== t83 || $[241] !== t85 || $[242] !== t87) {
    t88 = <button type="button" onClick={toggleHistoryCollapsed} aria-expanded={t83} className="flex w-full shrink-0 items-center justify-between px-5 py-2 text-xs font-medium uppercase tracking-wider text-muted-foreground transition-colors hover:text-foreground group-data-[collapsible=icon]:hidden">{t85}{t87}</button>;
    $[240] = t83;
    $[241] = t85;
    $[242] = t87;
    $[243] = t88;
  } else {
    t88 = $[243];
  }
  let t89;
  if ($[244] !== chats || $[245] !== currentChatId || $[246] !== editTitle || $[247] !== editingId || $[248] !== handleChatSelect || $[249] !== handleDeleteChat || $[250] !== handleTogglePin || $[251] !== historyRows || $[252] !== isHistoryCollapsed || $[253] !== isMobile || $[254] !== isPageLoading || $[255] !== pendingFocusChatId || $[256] !== pinnedChats || $[257] !== rowProps || $[258] !== saveTitle || $[259] !== t) {
    t89 = isHistoryCollapsed ? null : chats.length === 0 ? <div className="flex flex-col items-center justify-center py-8 text-center"><MessageSquare className="h-12 w-12 text-muted-foreground/50 mb-4" /><p className="text-sm text-muted-foreground mb-2">{t("playground.noChatHistory")}</p><p className="text-xs text-muted-foreground">{t("playground.noChatHistoryDescription")}</p></div> : <>{pinnedChats.length > 0 && <div className="shrink-0 max-h-[45%] overflow-y-auto border-b border-sidebar-border/60 pb-1"><div className="px-5 py-2 text-xs font-medium text-muted-foreground uppercase tracking-wider group-data-[collapsible=icon]:hidden">{t("playground.pinned")}</div>{pinnedChats.map(chat_5 => <div key={chat_5.id} style={{
          height: ROW_HEIGHT_CHAT
        }}><ChatHistoryItem chat={chat_5} currentChatId={currentChatId} editingId={editingId} editTitle={editTitle} pendingFocusChatId={pendingFocusChatId} isPageLoading={isPageLoading} isMobile={isMobile} onChatSelect={handleChatSelect} onEditTitleChange={setEditTitle} onSaveTitle={saveTitle} onCancelEdit={cancelEditTitle} onDeleteChat={handleDeleteChat} onTogglePin={handleTogglePin} onStartEdit={handleEditTitle} onEditFocused={onEditFocused} t={t} /></div>)}</div>}{historyRows.length > 0 && <List className="min-h-0 w-full flex-1" style={{
        width: "100%"
      }} rowComponent={ChatHistoryRowComponent} rowCount={historyRows.length} rowHeight={getChatHistoryRowHeight} rowProps={rowProps} overscanCount={8} />}</>;
    $[244] = chats;
    $[245] = currentChatId;
    $[246] = editTitle;
    $[247] = editingId;
    $[248] = handleChatSelect;
    $[249] = handleDeleteChat;
    $[250] = handleTogglePin;
    $[251] = historyRows;
    $[252] = isHistoryCollapsed;
    $[253] = isMobile;
    $[254] = isPageLoading;
    $[255] = pendingFocusChatId;
    $[256] = pinnedChats;
    $[257] = rowProps;
    $[258] = saveTitle;
    $[259] = t;
    $[260] = t89;
  } else {
    t89 = $[260];
  }
  let t90;
  if ($[261] !== isHistoryHidden || $[262] !== t88 || $[263] !== t89) {
    t90 = <div ref={listContainerRef} aria-hidden={isHistoryHidden} className="flex min-h-0 flex-1 flex-col transition-opacity duration-200 ease-linear group-data-[collapsible=icon]:pointer-events-none group-data-[collapsible=icon]:opacity-0">{t88}{t89}</div>;
    $[261] = isHistoryHidden;
    $[262] = t88;
    $[263] = t89;
    $[264] = t90;
  } else {
    t90 = $[264];
  }
  let t91;
  if ($[265] !== t82 || $[266] !== t90) {
    t91 = <SidebarContent className="overflow-hidden pb-2">{t82}{t90}</SidebarContent>;
    $[265] = t82;
    $[266] = t90;
    $[267] = t91;
  } else {
    t91 = $[267];
  }
  let t92;
  if ($[268] === Symbol.for("react.memo_cache_sentinel")) {
    t92 = <div className="px-2 pb-1 group-data-[collapsible=icon]:hidden"><LanguageSwitcher className="w-full" /></div>;
    $[268] = t92;
  } else {
    t92 = $[268];
  }
  const t93 = !selectedOrganization || selectedOrganization.isChat ? organization : selectedOrganization;
  const t94 = !selectedOrganization || selectedOrganization.isChat;
  let t95;
  if ($[269] !== isOrgLoading || $[270] !== t93 || $[271] !== t94) {
    t95 = <div className="group-data-[collapsible=icon]:hidden"><CreditsDisplay organization={t93} isLoading={isOrgLoading} isChatPlanOrg={t94} /></div>;
    $[269] = isOrgLoading;
    $[270] = t93;
    $[271] = t94;
    $[272] = t95;
  } else {
    t95 = $[272];
  }
  const t96 = user?.name ?? "User";
  let t97;
  if ($[273] !== user?.name) {
    t97 = user?.name?.split(" ").map(_temp8).join("").toUpperCase().slice(0, 2) ?? "U";
    $[273] = user?.name;
    $[274] = t97;
  } else {
    t97 = $[274];
  }
  let t98;
  if ($[275] !== t97) {
    t98 = <div className="flex aspect-square size-8 items-center justify-center rounded-lg bg-sidebar-primary text-sidebar-primary-foreground"><span className="text-xs font-semibold">{t97}</span></div>;
    $[275] = t97;
    $[276] = t98;
  } else {
    t98 = $[276];
  }
  const t99 = user?.name;
  let t100;
  if ($[277] !== t99) {
    t100 = <span className="truncate font-semibold">{t99}</span>;
    $[277] = t99;
    $[278] = t100;
  } else {
    t100 = $[278];
  }
  const t101 = user?.email;
  let t102;
  if ($[279] !== t101) {
    t102 = <span className="truncate text-xs text-muted-foreground">{t101}</span>;
    $[279] = t101;
    $[280] = t102;
  } else {
    t102 = $[280];
  }
  let t103;
  if ($[281] !== t100 || $[282] !== t102) {
    t103 = <div className="grid flex-1 text-left text-sm leading-tight">{t100}{t102}</div>;
    $[281] = t100;
    $[282] = t102;
    $[283] = t103;
  } else {
    t103 = $[283];
  }
  let t104;
  if ($[284] === Symbol.for("react.memo_cache_sentinel")) {
    t104 = <ChevronUp className="ml-auto size-4 group-data-[collapsible=icon]:hidden" />;
    $[284] = t104;
  } else {
    t104 = $[284];
  }
  let t105;
  if ($[285] !== t103 || $[286] !== t96 || $[287] !== t98) {
    t105 = <DropdownMenuTrigger asChild={true}><SidebarMenuButton size="lg" tooltip={t96} className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground">{t98}{t103}{t104}</SidebarMenuButton></DropdownMenuTrigger>;
    $[285] = t103;
    $[286] = t96;
    $[287] = t98;
    $[288] = t105;
  } else {
    t105 = $[288];
  }
  let t106;
  if ($[289] === Symbol.for("react.memo_cache_sentinel")) {
    t106 = process.env.NODE_ENV === "development" ? "http://localhost:3002/dashboard" : getMarketingUrl("/dashboard");
    $[289] = t106;
  } else {
    t106 = $[289];
  }
  let t107;
  if ($[290] === Symbol.for("react.memo_cache_sentinel")) {
    t107 = <ExternalLink className="mr-2 h-4 w-4" />;
    $[290] = t107;
  } else {
    t107 = $[290];
  }
  let t108;
  if ($[291] !== t) {
    t108 = t("playground.dashboard");
    $[291] = t;
    $[292] = t108;
  } else {
    t108 = $[292];
  }
  let t109;
  if ($[293] !== t108) {
    t109 = <DropdownMenuItem asChild={true}><a href={t106} target="_blank" rel="noopener noreferrer">{t107}{t108}</a></DropdownMenuItem>;
    $[293] = t108;
    $[294] = t109;
  } else {
    t109 = $[294];
  }
  let t110;
  if ($[295] === Symbol.for("react.memo_cache_sentinel")) {
    t110 = <DropdownMenuSeparator />;
    $[295] = t110;
  } else {
    t110 = $[295];
  }
  let t111;
  if ($[296] !== toggleTheme) {
    t111 = event => {
      event.preventDefault();
      toggleTheme();
    };
    $[296] = toggleTheme;
    $[297] = t111;
  } else {
    t111 = $[297];
  }
  let t112;
  if ($[298] !== t) {
    t112 = t("playground.theme");
    $[298] = t;
    $[299] = t112;
  } else {
    t112 = $[299];
  }
  let t113;
  if ($[300] !== t112) {
    t113 = <span>{t112}</span>;
    $[300] = t112;
    $[301] = t113;
  } else {
    t113 = $[301];
  }
  let t114;
  if ($[302] === Symbol.for("react.memo_cache_sentinel")) {
    t114 = <div onClick={_temp9} onKeyDown={_temp10}><ThemeToggle className="shrink-0" size="compact" /></div>;
    $[302] = t114;
  } else {
    t114 = $[302];
  }
  let t115;
  if ($[303] !== t111 || $[304] !== t113) {
    t115 = <DropdownMenuItem className="justify-between gap-3" onSelect={t111}>{t113}{t114}</DropdownMenuItem>;
    $[303] = t111;
    $[304] = t113;
    $[305] = t115;
  } else {
    t115 = $[305];
  }
  let t116;
  if ($[306] === Symbol.for("react.memo_cache_sentinel")) {
    t116 = <DropdownMenuSeparator />;
    $[306] = t116;
  } else {
    t116 = $[306];
  }
  let t117;
  if ($[307] === Symbol.for("react.memo_cache_sentinel")) {
    t117 = <LogOut className="mr-2 h-4 w-4" />;
    $[307] = t117;
  } else {
    t117 = $[307];
  }
  let t118;
  if ($[308] !== t) {
    t118 = t("playground.logOut");
    $[308] = t;
    $[309] = t118;
  } else {
    t118 = $[309];
  }
  let t119;
  if ($[310] !== logout || $[311] !== t118) {
    t119 = <DropdownMenuItem onClick={logout}>{t117}{t118}</DropdownMenuItem>;
    $[310] = logout;
    $[311] = t118;
    $[312] = t119;
  } else {
    t119 = $[312];
  }
  let t120;
  if ($[313] !== t109 || $[314] !== t115 || $[315] !== t119) {
    t120 = <DropdownMenuContent className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg" side="top" align="end" sideOffset={4}>{t109}{t110}{t115}{t116}{t119}</DropdownMenuContent>;
    $[313] = t109;
    $[314] = t115;
    $[315] = t119;
    $[316] = t120;
  } else {
    t120 = $[316];
  }
  let t121;
  if ($[317] !== t105 || $[318] !== t120) {
    t121 = <SidebarMenu><SidebarMenuItem><DropdownMenu>{t105}{t120}</DropdownMenu></SidebarMenuItem></SidebarMenu>;
    $[317] = t105;
    $[318] = t120;
    $[319] = t121;
  } else {
    t121 = $[319];
  }
  let t122;
  if ($[320] !== t121 || $[321] !== t95) {
    t122 = <SidebarFooter>{t92}{t95}{t121}</SidebarFooter>;
    $[320] = t121;
    $[321] = t95;
    $[322] = t122;
  } else {
    t122 = $[322];
  }
  let t123;
  if ($[323] !== t122 || $[324] !== t20 || $[325] !== t79 || $[326] !== t91) {
    t123 = <Sidebar collapsible="icon" className={t20}>{t79}{t91}{t122}</Sidebar>;
    $[323] = t122;
    $[324] = t20;
    $[325] = t79;
    $[326] = t91;
    $[327] = t123;
  } else {
    t123 = $[327];
  }
  return t123;
};
function _temp2() {
  return typeof document !== "undefined" && document.cookie.split("; ").includes(`${HISTORY_COLLAPSED_STORAGE_KEY}=1`);
}
function _temp3(prev) {
  const next = !prev;
  document.cookie = `${HISTORY_COLLAPSED_STORAGE_KEY}=${next ? "1" : "0"}; path=/; max-age=${31536000}; samesite=lax`;
  return next;
}
function _temp4(a, b) {
  return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
}
function _temp5(chat_2) {
  return chat_2.pinned;
}
function _temp6(chat_3) {
  return !chat_3.pinned;
}
function _temp7(group) {
  return group.chats.length > 0;
}
function _temp8(n) {
  return n[0];
}
function _temp9(event_0) {
  return event_0.stopPropagation();
}
function _temp10(event_1) {
  return event_1.stopPropagation();
}